import { Storage } from '../Utils/Constants';
/**
 * @class
 * @description Class that stores downloaded segments so they can be shared.
 * It automatically removes old and unused segments when needed.
 * @exports SegmentStorage
 */
export default class SegmentStorage {
    /**
     * Constructs Storage object.
     * @param {Loader} loader Loader object.
     */
    constructor(loader) {
        this._localMem = new Map();
        this._maxSize = Storage.count;
        this._expTime = Storage.expiration;
        this._loader = loader;
        setInterval(() => {
            this._removeOldSegments();
        }, 1000);
    }
    /**
     * Set the options of the storage, using API response.
     * @param {balancerResponse} e API response
     * @public
     */
    setSettings(e) {
        const p2p = e.p2p;
        if (p2p) {
            this._expTime = p2p.cachedSegmentExpiration || Storage.expiration;
            this._maxSize = Math.min(p2p.cachedSegmentsCount || Storage.count, Storage.safeMaxCount);
        }
    }
    /**
     * Checks in the storage for the requested segment and returns it. Undefined if is not there.
     * When returned updates the useTime variable of the segment so it wont be auto-deleted.
     * @param {string} id Id of the segment.
     * @returns {VideoSegment|undefined} Segment object corresponding to the id, or undefined.
     * @public
     */
    getSegment(id) {
        return this._localMem.get(id);
    }
    /**
     * Checks in the storage for the requested segment and returns if its there or not.
     * @param {string} id Id of the segment.
     * @returns {boolean} Availability of the chosen segment.
     * @private
     */
    _hasSegment(id) {
        return this._localMem.has(id);
    }
    /**
     * Stores the given segment.
     * If size exceeds the maximum it will delete the oldest segment.
     * @param {VideoSegment} newSegment Segment object to store.
     * @param {ArrayBuffer} data Array buffer with the data of the segment.
     * @param {boolean} update If it should replace previous values, false by default.
     * @public
     */
    storeSegment(segment) {
        if (!this._hasSegment(segment.p2pId) && typeof segment.data !== 'undefined') {
            this._localMem.set(segment.p2pId, segment);
            this._reduceMemSize();
            return true;
        }
        return false;
    }
    /**
     * Calls recursively every second to check for unused/old segments and delete them.
     * @private
     */
    _removeOldSegments() {
        const expThreshold = Date.now() - this._expTime;
        this._localMem.forEach((value, key) => {
            if (value.startTime < expThreshold) {
                this._localMem.delete(key);
            }
        });
    }
    /**
     * Checks for the memory size (number of segments) and deletes oldest ones if size exceeded the maximum.
     * @private
     */
    _reduceMemSize() {
        var _a, _b;
        const overSize = this._localMem.size - this._maxSize;
        let ind;
        let minTime;
        if (overSize > 0) {
            minTime = Date.now();
            ind = '';
            this._localMem.forEach((value, key) => {
                if (value.endTime < minTime) {
                    ind = key;
                    minTime = value.endTime;
                }
            });
            if (((_a = this._localMem.get(ind)) === null || _a === void 0 ? void 0 : _a.activeP2PSendProcesses) == 0)
                console.log("Delete storage -> " + ((_b = this._localMem.get(ind)) === null || _b === void 0 ? void 0 : _b.origRes) + " size-> " + this._localMem.size);
            this._localMem.delete(ind);
            this._reduceMemSize();
        }
    }
    /**
     * Generates a map for peers. An object with key name equal to manifest url, an object as a value.
     * This array contains as keys the path names of the video segments and as value its status
     * (0 downloaded, 1 downloading)
     * @returns {map} Map of the available chunks to share with peers.
     * @public
     */
    createMap() {
        const segments = [];
        for (const [, segment] of this._localMem) {
            if (segment.readyForP2P)
                segments.push(segment.p2pId);
        }
        return segments;
    }
}
